import { useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2 } from 'lucide-react';
import { useGetFeedTeamsQuery, useGetFeedPlayersQuery } from '@/api/feed';
import type { FixtureSelection } from '../QuestionCoachModal';

interface PlayerOverUnderFixtureCardProps {
  fixture: FixtureSelection;
  teamMap: Map<string, string>;
  /** Minimum players required for this fixture — 1 for Player Over/Under, 2 for Player Selection. */
  minPlayers: number;
  onSelectTeam: (teamId: string) => void;
  onTogglePlayer: (playerId: string, playerName: string, checked: boolean) => void;
}

/**
 * One fixture's player picker (DKPLAT-501 Phase 2/3 — shared by Player Over/Under
 * and Player Selection). Both teams are shown as roster tabs — switching tabs
 * only changes which roster is listed; player selections accumulate across both
 * teams. Each fixture card is its own component so the players query (which
 * depends on the roster viewed for THIS fixture) can be called unconditionally,
 * satisfying the rules of hooks even though the number of fixture cards varies.
 */
function PlayerOverUnderFixtureCard({
  fixture,
  teamMap,
  minPlayers,
  onSelectTeam,
  onTogglePlayer,
}: PlayerOverUnderFixtureCardProps) {
  // The viewed roster defaults to the first (home) team until the user switches.
  const viewedTeamId = fixture.selectedTeamIdForPlayer ?? fixture.fixtureTeamIds[0];
  const { data: players, isLoading, error } = useGetFeedPlayersQuery(viewedTeamId);
  const selectedPlayerIds = fixture.playerIds ?? [];
  const selectedPlayerNames = fixture.playerNames ?? [];

  const teamName = teamMap.get(viewedTeamId) || viewedTeamId;

  return (
    <div className="space-y-2">
      <Label className="text-sm font-semibold">{fixture.fixtureDisplay}</Label>
      <div className="flex gap-2">
        {fixture.fixtureTeamIds.map(teamId => (
          <Button
            key={teamId}
            data-testid={`player-ou-team-${fixture.fixtureId}-${teamId}`}
            variant={teamId === viewedTeamId ? 'default' : 'outline'}
            size="sm"
            onClick={() => onSelectTeam(teamId)}
          >
            {teamMap.get(teamId) || teamId}
          </Button>
        ))}
      </div>
      <p className="text-sm text-muted-foreground">
        Select players from {teamName} — switch team to add players from both sides.
      </p>

      {isLoading ? (
        <div className="flex items-center gap-2 py-2">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Loading players...</span>
        </div>
      ) : error ? (
        <Alert variant="destructive">
          <AlertDescription>Failed to load players. Please try again.</AlertDescription>
        </Alert>
      ) : !players || players.length === 0 ? (
        <Alert>
          <AlertDescription>No player data available for {teamName}.</AlertDescription>
        </Alert>
      ) : (
        <div className="space-y-2 max-h-[250px] overflow-y-auto">
          {players.map(player => {
            const isSelected = selectedPlayerIds.includes(player.externalId);
            return (
              <Card
                key={player.externalId}
                data-testid={`player-${fixture.fixtureId}-${player.externalId}`}
                className={`p-3 cursor-pointer hover:bg-accent ${isSelected ? 'ring-2 ring-primary bg-primary/5' : ''}`}
                onClick={() => onTogglePlayer(player.externalId, player.displayName, !isSelected)}
              >
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={checked =>
                      onTogglePlayer(player.externalId, player.displayName, checked === true)
                    }
                    // Stop the click from bubbling to the Card's onClick, which
                    // would toggle the player straight back off.
                    onClick={e => e.stopPropagation()}
                  />
                  <div className="flex-1">
                    <div className="font-medium">{player.displayName}</div>
                    {player.position && (
                      <div className="text-sm text-muted-foreground">{player.position}</div>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {selectedPlayerIds.length < minPlayers ? (
        <Alert>
          <AlertDescription>
            Select at least {minPlayers} player{minPlayers === 1 ? '' : 's'} for this fixture.
          </AlertDescription>
        </Alert>
      ) : (
        // Keeps selections from the non-viewed roster visible after switching teams.
        <p className="text-sm text-muted-foreground">
          Selected ({selectedPlayerIds.length}): {selectedPlayerNames.join(', ')}
        </p>
      )}
    </div>
  );
}

interface PlayerOverUnderStepProps {
  leagueId?: string;
  fixtures: FixtureSelection[];
  /**
   * Minimum players required per fixture — 1 for Player Over/Under (default),
   * 2 for Player Selection (MIN_TEAMS.SELECTION). Also affects the per-fixture
   * validation copy.
   */
  minPlayers?: number;
  /** Copy overrides — Player Selection explains the "one question per fixture" semantics. */
  title?: string;
  description?: string;
  onSelectTeamForFixture: (fixtureId: string, teamId: string) => void;
  onTogglePlayerForFixture: (
    fixtureId: string,
    playerId: string,
    playerName: string,
    checked: boolean
  ) => void;
}

/**
 * Batch creation mode (DKPLAT-501): one group per selected fixture, shared by
 * Player Over/Under (Phase 2, one question per fixture x player, minPlayers=1)
 * and Player Selection (Phase 3, one question per fixture with all selected
 * players as its options, minPlayers=2). Per fixture: free multi-select of
 * players from either team's roster (roster tabs, selections accumulate).
 */
export function PlayerOverUnderStep({
  leagueId,
  fixtures,
  minPlayers = 1,
  title = 'Select players for each fixture',
  description = 'Choose one or more players from either team — a separate question will be created for each player selected.',
  onSelectTeamForFixture,
  onTogglePlayerForFixture,
}: PlayerOverUnderStepProps) {
  const {
    data: teams,
    isLoading: teamsLoading,
    error: teamsError,
  } = useGetFeedTeamsQuery(leagueId);

  const teamMap = useMemo(() => {
    const map = new Map<string, string>();
    teams?.forEach(team => map.set(team.externalId, team.name));
    return map;
  }, [teams]);

  if (teamsLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading teams...</span>
      </div>
    );
  }

  if (teamsError) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Failed to load teams. Please try again.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Label className="text-base mb-2 block">{title}</Label>
        <p className="text-sm text-muted-foreground mb-4">{description}</p>
      </div>

      {fixtures.map(fixture => (
        <PlayerOverUnderFixtureCard
          key={fixture.fixtureId}
          fixture={fixture}
          teamMap={teamMap}
          minPlayers={minPlayers}
          onSelectTeam={teamId => onSelectTeamForFixture(fixture.fixtureId, teamId)}
          onTogglePlayer={(playerId, playerName, checked) =>
            onTogglePlayerForFixture(fixture.fixtureId, playerId, playerName, checked)
          }
        />
      ))}
    </div>
  );
}
